import { Button, Link } from '@material-ui/core';
import { Animal } from 'api/query/animal/animal';
import animalApi from 'api/query/animal/animal-api';
import { ASBV } from 'api/query/asbv/asbv';
import Axios from 'axios';
import ASBVDisclaimer from 'components/asbv-disclaimer';
import LoadContainer from 'components/load-container';
import { getDatabaseTitle } from 'pages/search/dashboard/dashboard-utils';
import React, { useEffect, useState } from 'react';
import { getCustomASBVsFromStorage } from 'utils/asbv-utils';
import FormatLPID from 'utils/formatting';
import AllASBVs from './all-asbvs';
import { AnimalDetailsProps } from './animal-details-props';
import './animal-details.scss';
import ASBVSnapshot from './asbv-snapshot';
import Overview from './overview';
import PedigreeChart from './pedigree-chart';
import TraitGroups from './trait-groups';
import TraitsObserved from './traits-observed';

/**
 * The root component for the animal details page.
 * @param props
 */
export const AnimalDetails: React.FC<AnimalDetailsProps> = (props) => {
  const {
    id,
    db,
    subIndex,
    asbvs,
  } = props;

  const [animal, setAnimal] = useState<Animal>();
  const [asbvIds, setASBVIds] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    const getAnimalDetails = async () => {
      try {
        let ids = asbvs;
        // If ASBVs were provided as part of the URL, use those.
        // If none were provided, try get the user cache/filter ASBVs.
        if (!asbvs) {
          const cacheASBVs = getCustomASBVsFromStorage(db, subIndex);
          if (cacheASBVs) {
            ids = cacheASBVs.map((asbv: ASBV) => asbv.asbvId).toString();
          }
        }
        const data = await animalApi.getByKey(id, db, subIndex, ids, source.token);
        setLoading(false);
        setASBVIds(ids);
        setAnimal(data);
      } catch (e) {
        source.cancel();
        setLoading(false);
      }
    };

    getAnimalDetails();

    return () => {
      source.cancel();
    };
  }, [asbvs, db, id, subIndex]);

  return (
    <div className="sg-animal-details">
      <LoadContainer
        loading={loading}
      >
        {animal ? (
          <>
            <Overview
              animal={animal}
            />
            <PedigreeChart
              animal={animal}
              db={db}
              subIndex={subIndex}
              asbvIds={asbvIds}
            />
            <ASBVSnapshot
              animals={[{ id: animal.animalId, asbvs: animal.asbvs }]}
            />
            <AllASBVs
              indexes={animal.indexes}
              db={db}
              subIndex={subIndex}
            />
            <TraitGroups
              id={animal.animalId}
              db={db}
              subIndex={subIndex}
              asbvIds={asbvIds}
            />
            <TraitsObserved
              id={animal.animalId}
              subIndex={subIndex}
              db={db}
            />
            <ASBVDisclaimer />
          </>
        ) : (
          <>
            <div className="sg-page-not-found">
              <img
                src="/images/404.svg"
                alt="404"
              />
              <h5>Animal {FormatLPID(id)} not found in {getDatabaseTitle(db)} analysis</h5>
              <p>This animal could be set to private, it doesn’t exist or belongs to a different analysis.</p>
              <Link
                href="/search/dashboard"
                underline="none"
              >
                <Button>Back to dashboard</Button>
              </Link>
            </div>
          </>
        )}
      </LoadContainer>
    </div>
  );
};

(AnimalDetails as any).whyDidYouRender = true;
